import React, {useState} from "react";
import {
  Typography,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import { StyledTextField } from "./styles";
import TabPanel from "./TabPanel";
import QuestionItem from "./QuestionItem";
import {useUI} from "context/ui/uiContext";
import ImageUploadButton from "./Pictures/ImageUploadButton";
import { CheckCircleOutline, WarningAmberOutlined } from "@mui/icons-material";

import odo_illustration from "assets/illustrations/odo.jpeg";
import left_side_illustration from "assets/illustrations/left_side.jpg";
import right_side_illustration from "assets/illustrations/right_side.jpg";
import rear_illustration from "assets/illustrations/rear.jpg";
import key_fob_illustration from "assets/illustrations/key_fob.jpg";
import gas_cap_illustration from "assets/illustrations/gas_cap.jpg";
import rear_tire_illustration from "assets/illustrations/rear_tire.jpg";
import front_tire_illustration from "assets/illustrations/front_tire.jpg";
import {AccountBoxRounded, LightbulbRounded} from "@mui/icons-material";
import useLocalStorage from "hooks/useLocalStorage";
import clocks_illustration from "../../assets/illustrations/clocks.jpg";
import {useIsMobile} from "hooks/useIsMobile";

const illustrationMap = {
  "Right side": right_side_illustration,
  "Front": front_tire_illustration,
  "Left side": left_side_illustration,
  "Rear": rear_illustration,
  "Odometer": odo_illustration,
  "Oil Pressure": clocks_illustration,
  "Key Fobs": key_fob_illustration,
  "Under Gas Cap": gas_cap_illustration,
  "Front tire tread": front_tire_illustration,
  "Rear tire tread": rear_tire_illustration,
  "VIN": left_side_illustration,
};

const groupOptionsByGroupName = (options) => {
  const grouped = options.reduce((groups, option) => {
    const group = option.groupName;
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(option);
    return groups;
  }, {});

  // Sort each group's options by displayOrder in ascending order
  Object.keys(grouped).forEach(groupName => {
    grouped[groupName].sort((a, b) => a.displayOrder - b.displayOrder);
  });

  return grouped;
};

const getGroupNameByTabIndex = (tabIndex) => {
  switch (tabIndex) {
  case 0:
    return "Mechanical";
  case 1:
    return "Cosmetic";
  case 2:
    return "Accessories";
  default:
    return null;
  }
};

/**
 * @param {number} index - The index of the tab which is used to create unique IDs for accessibility attributes.
 * @returns {object} An object containing accessibility attributes (`id` and `aria-controls`) for a tab element.
 */
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

/**
 * ConditionRating is a functional React component that renders the inspection form for assessing
 * various aspects of a motorcycle's condition. It is organized into tabs that categorize the inspection
 * criteria into mechanical, cosmetic, accessories, and pictures for documentation. The component
 * handles data fetching, navigation, and form state management.
 * @param root0
 * @param root0.inspection
 * @param root0.submitConditionReport
 * @param root0.vehicle
 * @param root0.isDealer
 * @param root0.children
 * @param root0.addImage
 * @param root0.images
 * @param root0.tabChangeCallback
 * @param root0.removeImage
 * @returns {React.Element} A React component that renders a grid layout with side navigation and multiple
 *                        tabs containing form elements for different inspection criteria. Each tab contains
 *                        a list of questions related to the motorcycle's condition, options for responses,
 *                        and the ability to navigate through the tabs or submit the inspection data.
 *
 * Features:
 * - Fetches master data based on customer vehicle details when the component mounts.
 * - Manages state for form values, selected options, and current tab index.
 * - Handles option changes within the form and updates the state accordingly.
 * - Supports navigation to a confirmation or next steps page upon form submission.
 * - Provides a structured layout with side navigation and tabbed content areas for organized data entry.
 *
 * Usage:
 * This component is intended to be used as part of a larger application where vehicle inspection data
 * needs to be collected and processed. It expects to operate within a context where `useCustomer` and
 * `useNavigate` hooks are available to supply necessary data and navigation capabilities.
 */
function ConditionRating({inspection, submitConditionReport, vehicle, isDealer = false, children, addImage, removeImage, images, tabChangeCallback = null}) {
  const vehicleID = vehicle?.vehicleId || vehicle?.vehicleID;
  const [mileageError, setMileageError] = useState(false);
  const [selectedOptions, setSelectedOptions] = useLocalStorage(`selectedOptions_${vehicleID}_${isDealer}`, "");
  const [mileage, setMileage] = useLocalStorage(`mileage_${vehicleID}_${isDealer}`, "");
  const [currentTab, setCurrentTab] = useLocalStorage(`currentTab_${vehicleID}_${isDealer}`, 0); //React.useState(0);
  const {showAlert, showLoadingSpinner, hideLoadingSpinner} = useUI();
  const {inspectionPictureQueries} = inspection;
  const isMobile = useIsMobile();
  const {customerInspectionEntity} = vehicle;

  const groupedOptions = groupOptionsByGroupName(inspection?.inspectionOptions || {});

  // Assuming you have an array of tab labels
  const tabLabels = ["Mechanical", "Cosmetic", "Accessories"];

  const handleSubmit = () => {
    if (!allQuestionsAnswered()) {
      showAlert("Please answer all questions before submitting.", "error");
      return;
    }

    submitConditionReport(selectedOptions, mileage);
  };

  const handleOptionChange = (rating, name, isParent) => {
    setSelectedOptions(prev => ({ ...prev, [name]: {rating, isParent} }));
  };

  // Handles sub-option selection
  const handleSubOptionChange = ({rating, name, parent, type}) => {
    console.log("rating:",rating);



    // console.log("rating, name, parent, type", rating, name, parent, type);
    setSelectedOptions(prev => {
      const updatedOptions = { ...prev };

      // Ensure the main index exists in the array
      if (!updatedOptions[parent]) {
        updatedOptions[parent] = {
          name: name,
          rating: "",
          inspectionSubQueries: []
        };
      } else {
        // Ensure immutability for inspectionSubQueries
        updatedOptions[parent] = {
          ...updatedOptions[parent],
          inspectionSubQueries: { ...updatedOptions[parent].inspectionSubQueries }
        };
      }

      if (type !== "OPEN TEXT") {
        const currentElement = updatedOptions[parent]?.inspectionSubQueries;
        let currentRatings = currentElement?.ratings || [];

        if (rating === "NONE APPLICABLE") {
          // If "NONE APPLICABLE" is selected, clear all other ratings and set it
          currentRatings = ["NONE APPLICABLE"];
        } else {
          // For any other rating, remove "NONE APPLICABLE" if it exists
          currentRatings = currentRatings.filter(r => r !== "NONE APPLICABLE");

          // Check if the rating already exists
          const ratingIndex = currentRatings.indexOf(rating);
          if (ratingIndex === -1) {
            // Add rating if it does not exist
            currentRatings.push(rating);
          } else {
            // Remove rating if it exists
            currentRatings = currentRatings.filter(r => r !== rating);
          }
        }

        // Update the ratings in updatedOptions
        updatedOptions[parent].inspectionSubQueries = {
          name,
          "selectType": type,
          ratings: currentRatings
        };
      } else {
        console.log("open text");
        updatedOptions[parent].inspectionSubQueriesText = {
          name,
          selectType: "Open Text",
          ratings: [`${rating}`]
        };
      }

      console.log("updatedOptions:", updatedOptions);
      return updatedOptions;
    });
  };


  const handleTabChange = (event, newValue) => {
    if (canProceedToTab(newValue)) {
      window.scrollTo({
        top: 0,
      });
      setCurrentTab(newValue);
      if (tabChangeCallback) {
        tabChangeCallback(newValue);
      }
    }
  };

  const handleMoveNext = (index) => {
    // Check if mileage is valid before proceeding to the next tab
    if (index === 1 && (!mileage || mileage <= 0)) {
      setMileageError(true);
      showAlert("Please enter a valid mileage greater than 0.", "error");
      return;
    }

    if (canProceedToTab(index)) {
      setCurrentTab(index);
      if (tabChangeCallback) tabChangeCallback(index);
      window.scrollTo({top: 0});
    }
  };

  const allQuestionsAnswered = () => {
    return inspection?.inspectionOptions.every((option) => {
      return selectedOptions[option.name]?.rating !== undefined;
    });
  };

  const allQuestionsAnsweredInTab = (groupName) => {
    let subQuestionsAnswered = true;

    groupedOptions[groupName]?.forEach((option) => {
      const optionData = selectedOptions[option.name];
      if (optionData?.isParent) {
        subQuestionsAnswered =
          (Array.isArray(optionData?.inspectionSubQueries?.ratings) && optionData?.inspectionSubQueries?.ratings.length > 0) ||
          (Array.isArray(optionData?.inspectionSubQueriesText?.ratings) && optionData?.inspectionSubQueriesText?.ratings[0].length > 0);
      }
    });

    return groupedOptions[groupName]?.every((option) => {
      return selectedOptions[option.name]?.rating !== undefined && subQuestionsAnswered;
    });
  };

  const canProceedToTab = (index) => {
    // Logic to check if all questions in the current tab are answered
    if (index === 0) return true;
    if (index === 1) return allQuestionsAnsweredInTab("Mechanical");
    if (index === 2) return allQuestionsAnsweredInTab("Cosmetic");
    if (index === 3) return allQuestionsAnsweredInTab("Accessories");
    return false;
  };

  const remainingQuestionsInTab = (groupName) => {
    return groupedOptions[groupName]?.filter((option) => {
      return selectedOptions[option.name]?.rating === undefined;
    }).length;
  };

  const allImagesUploaded = () => {
    return true;
  };

  return (
    <Grid container spacing={isMobile ? 0 : 2}
      justifyContent="center"
      alignItems="start"
      style={{ margin: "25px 0" }}>
      {children}

      <Grid item xs={12} md={8}>
        <Paper elevation={3} style={{ padding: "15px", margin: "10px 0" }}>
          <Typography variant="h5" gutterBottom>
            Condition Rating
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={currentTab} onChange={handleTabChange} aria-label="Condition tabs">
              <Tab
                label={
                  <span>
        Mechanical
                    {allQuestionsAnsweredInTab("Mechanical") ? (
                      <CheckCircleOutline
                        style={{ color: "green", marginLeft: 8, verticalAlign: "middle" }}
                        fontSize="small"
                      />
                    ) : (
                      <WarningAmberOutlined
                        style={{ color: "gray", marginLeft: 8, verticalAlign: "middle" }}
                        fontSize="small"
                      />
                    )}
                  </span>
                }
                {...a11yProps(0)}
                disabled={!canProceedToTab(0)}
              />
              <Tab
                label={
                  <span>
        Cosmetic
                    {allQuestionsAnsweredInTab("Cosmetic") ? (
                      <CheckCircleOutline
                        style={{ color: "green", marginLeft: 8, verticalAlign: "middle" }}
                        fontSize="small"
                      />
                    ) : (
                      <WarningAmberOutlined
                        style={{ color: "gray", marginLeft: 8, verticalAlign: "middle" }}
                        fontSize="small"
                      />
                    )}
                  </span>
                }
                {...a11yProps(1)}
                disabled={!canProceedToTab(1)}
              />
              <Tab
                label={
                  <span>
        Accessories
                    {allQuestionsAnsweredInTab("Accessories") ? (
                      <CheckCircleOutline
                        style={{ color: "green", marginLeft: 8, verticalAlign: "middle" }}
                        fontSize="small"
                      />
                    ) : (
                      <WarningAmberOutlined
                        style={{ color: "gray", marginLeft: 8, verticalAlign: "middle" }}
                        fontSize="small"
                      />
                    )}
                  </span>
                }
                {...a11yProps(2)}
                disabled={!canProceedToTab(2)}
              />
              <Tab
                label={
                  <span>
        Pictures
                    {/*{allImagesUploaded() ? (*/}
                    {/*  <CheckCircleOutline*/}
                    {/*    style={{ color: "green", marginLeft: 8, verticalAlign: "middle" }}*/}
                    {/*    fontSize="small"*/}
                    {/*  />*/}
                    {/*) : (*/}
                    {/*  <WarningAmberOutlined*/}
                    {/*    style={{ color: "gray", marginLeft: 8, verticalAlign: "middle" }}*/}
                    {/*    fontSize="small"*/}
                    {/*  />*/}
                    {/*)}*/}
                  </span>
                }
                {...a11yProps(3)}
                disabled={!canProceedToTab(3)}
              />
            </Tabs>


          </Box>
          <TabPanel value={currentTab} index={0}>
            <Typography variant="h6">Mileage:</Typography>
            <StyledTextField
              type="number"
              size="small"
              value={mileage}
              onChange={(e) => {
                setMileage(e.target.value);
                setMileageError(false); // Reset error on change
              }}
              error={mileageError} // Highlight the field if there's an error
              helperText={mileageError ? "Mileage must be greater than 0" : ""}
            />

            {groupedOptions["Mechanical"]?.map((option, mainIndex) => (
              <QuestionItem
                key={mainIndex}
                selectedOptions={selectedOptions}
                handleOptionChange={handleOptionChange}
                handleSubOptionChange={handleSubOptionChange}
                mainIndex={mainIndex}
                option={option}
                previousInspection={customerInspectionEntity}
              />))}

          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            {groupedOptions["Cosmetic"]?.map((option, mainIndex) => (
              <QuestionItem
                key={mainIndex + 6}
                selectedOptions={selectedOptions}
                handleOptionChange={handleOptionChange}
                handleSubOptionChange={handleSubOptionChange}
                mainIndex={mainIndex + 6}
                option={option}
                previousInspection={customerInspectionEntity}
              />))}

          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            {groupedOptions["Accessories"]?.map((option, mainIndex) => (
              <QuestionItem
                key={mainIndex + 12}
                selectedOptions={selectedOptions}
                handleOptionChange={handleOptionChange}
                handleSubOptionChange={handleSubOptionChange}
                mainIndex={mainIndex + 12}
                option={option}
                previousInspection={customerInspectionEntity}
              />))}

          </TabPanel>

          <TabPanel value={currentTab} index={3}>
            <Grid container spacing={2}>
              {inspectionPictureQueries && inspectionPictureQueries.map((image, key) => (
                <Grid item xs={12} sm={6} md={4} key={`image_${key}`}>
                  <ImageUploadButton
                    image={images?.[image.text] || null}
                    label={image.text}
                    vehicleID={vehicleID}
                    placeholder={illustrationMap[image.name] ? illustrationMap[image.name] : left_side_illustration}
                    addImage={addImage}
                    removeImage={removeImage}
                  />
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* Fixed Next/Submit Button at the Bottom */}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "calc(100% - 16px)",
              p: 2,
              backgroundColor: "white",
              borderTop: "1px solid #ddd",
              textAlign: "center",
              zIndex: 10
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              style={{ marginBottom: "10px" }}
              onClick={currentTab === 3 ? handleSubmit : () => handleMoveNext(currentTab + 1)}
              disabled={
                currentTab === 3
                  ? !allQuestionsAnsweredInTab("Accessories") || !allImagesUploaded()
                  : !allQuestionsAnsweredInTab(getGroupNameByTabIndex(currentTab))
              }
            >
              {currentTab === 3 ? "Finish" : "Continue"}
            </Button>

            {remainingQuestionsInTab(getGroupNameByTabIndex(currentTab)) > 0 && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {`${remainingQuestionsInTab(getGroupNameByTabIndex(currentTab))} questions in this section`}
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>

    </Grid>
  );
}

export default ConditionRating;
